import React, { useEffect, useRef, useState } from "react";
import {
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Grid,
  Alert,
  Box,
  styled,
  Tooltip,
} from "@mui/material";
import { IResolveExceptionDialogProps } from "../../../interfaces";
import Icon from "@mdi/react";
import { mdiAlertCircleOutline, mdiCheck, mdiClose, mdiSquareEditOutline } from "@mdi/js";
import { ConfirmationDialog, DialogLoader, DialogStyle } from "../../../components";
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  GridRowId,
  useGridApiRef,
  GridColumnVisibilityModel,
  GridApi,
} from "@mui/x-data-grid";
import styles from "./resolveExceptionDialog.module.scss";
import ContainerizedDetailService from "../../../api/services/ContainerizedDetailService";
import { ContainerType } from "../../../utils/Enum/InventoryExceptionType";
import { ExceptionTypes } from "../../../utils/ExceptionTypes";
import { useClient } from "../../../utils/ClientContext";
const ResolveExceptionDialog: React.FC<IResolveExceptionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  containarizeDetailId,
  containerType,
  tagId,
  selectedValue
}) => {
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState<"success" | "error">("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateCount, setUpdateCount] = useState<number>(0);
  const [showLoader, setShowLoader] = useState(false);
  const [showTableLoader, setShowTableLoader] = useState(false);
  const [isRowValid, setIsRowValid] = useState<Record<GridRowId, boolean>>({});
  const saveIntentRef = useRef<{[key: string]: boolean}>({});

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
  id: false,
  qty: true // default
});
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState<GridRowsProp>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const inventoryId = Number(localStorage.getItem("inventoryId"));
    const { client } = useClient();
    const clientId = client.id;
    const userId = Number(localStorage.getItem("userId"));
  const [accepDialogOpen, setAcceptDialogOpen] = useState(false);

//<-- Start: Hooks -->
useEffect(() => {
  const anyNonSerialized = rows.some(r => !r.isSerialized);
  const isAnySerial = rows.some(r => r.isSerialized);
  const isAnyLot = rows.some(r => r.isLotted);
  const isAnyCarton = rows.some(r => r.isCartoned);
  const isAnyPallet = rows.some(r => r.isPalletized);
  apiRef.current?.setColumnVisibility('qty', anyNonSerialized);
  setColumnVisibilityModel((prev:any) => ({
    ...prev,
    pallet: isAnyPallet,
    carton: isAnyCarton,
    lot: isAnyLot,
    serialNo: isAnySerial,
    qty: anyNonSerialized
  }));
}, [rows, apiRef]);
useEffect(() => {
setUpdateCount(0);
if (open) {
  fetchData();
}
else{
setRows([]);
}
}, [open, containarizeDetailId, tagId, inventoryId, containerType]);
//<-- End: Hooks -->

//<-- Start: Logic And API Communication -->
  const fetchData = async (isSubmit?: boolean): Promise<void> => {
    if(!isSubmit){
      setShowLoader(true);
    }
  try {
    const response = await ContainerizedDetailService.getExceptionsByField(
      inventoryId,
      containarizeDetailId,
      containerType,
      selectedValue
    );

    const success = response?.data?.isSuccess;
    const items = response?.data?.containerExceptionDetails || [];

    if (success) {
      const sanitizedRows = items.map((row: any) => ({
        ...row,
        pallet: row.pallet ?? null,
        carton: row.carton ?? null,
        lot: row.lot ?? null,
        serialNo: row.serialNo ?? null,
      }));

      if (isSubmit && onSubmit && sanitizedRows.length === 0) {
        setTimeout(onSubmit, 2000);
      }

      // Move matching record to the top
      sanitizedRows.sort((a: any, b: any) =>
        a.id === containarizeDetailId ? -1 :
        b.id === containarizeDetailId ? 1 : 0
      );

      setRows(sanitizedRows);
    } else {
      setRows([]);
    }
  } catch (err) {
    console.error("fetchData error:", err);
    setRows([]);
  } finally {
    setShowLoader(false);
  }
};
const handleAcceptException = async () => {
      setShowTableLoader(true);
      //setShowLoader(true);
      const request = {
      inventoryId,
      ContainerId: containarizeDetailId,
      containerType,
      acceptedValue:selectedValue,
      userId
      }
  try {
    const response = await ContainerizedDetailService.acceptException(request);

    const success = response?.data?.isSuccess;

    if (success) {
      setUpdateCount(updateCount+1);
      toasterMessage("success", "Exception accepted successfully!");
    } else {
      toasterMessage("error", "Something went wrong.");
    }
  } catch (err) {
    console.error("fetchData error:", err);
    setRows([]);
  } finally {
    setShowTableLoader(false);
    //setShowLoader(false);
    fetchData(true);
  }
}
const toasterMessage=(severity:"success" | "error", msg: string)=>{
  setSnackbarMessage(msg);
      setSeverity(severity);
      setOpenSnackbar(true);
      setTimeout(() => {
        handleCloseSnackbar();
      }, 2000);  
}
//<-- End: Logic And API Communication -->

//<-- Start: Popup events -->
const handleCloseSnackbar = () => {
  setOpenSnackbar(false);
};
const handleClickClose = () => {
  
  if(updateCount > 0 && onSubmit){
    onSubmit();
  }
  else{
  onClose?.();
  }
};
const getIcon = () => (
  <Icon
    path={severity === "success" ? mdiCheck : mdiAlertCircleOutline}
    size={1}
  />
);
//<-- End: Popup events -->

//<-- Start: Table properties and events -->
function handleSaveClick(id: GridRowId) {
    saveIntentRef.current[id] = true;
  apiRef.current?.stopRowEditMode({ id, ignoreModifications: false });
}
const handleEditClick = (id: GridRowId) => {
  // 1. Find any other row that’s in Edit mode
  const editingEntry = Object.entries(rowModesModel).find(
    ([rowId, modeProps]) =>
      modeProps.mode === GridRowModes.Edit && rowId !== String(id)
  );

  if (editingEntry) {
    const [editingId] = editingEntry;
    // 2. Cancel its edits (ignore any modifications)
    apiRef.current?.stopRowEditMode({ id: editingId, ignoreModifications: true });
    setRowModesModel((prev) => ({
      ...prev,
      [editingId]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  }

  // 3. Now start edit on the clicked row
  apiRef.current?.startRowEditMode({ id });
  setRowModesModel((prev) => ({
    ...prev,
    [id]: {
      mode: GridRowModes.Edit,
      fieldToFocus: getToBeFocusedField(id),
    },
  }));
};
function handleCancelClick(id: GridRowId) {
  saveIntentRef.current[id] = false;
  apiRef.current?.stopRowEditMode({ id, ignoreModifications: true });
}
async function processRowUpdate(newRow: any, oldRow: any) {
    if (!saveIntentRef.current[newRow.id]) {
    return oldRow;
  }
  saveIntentRef.current[newRow.id] = false;
   if (newRow.isLotted && (!newRow.lot?.trim() ||newRow.lot?.trim().length > 50)) {
    throw new Error("Lot number is invalid.");
  }
  if (newRow.isSerialized && (!newRow.serialNo?.trim() ||newRow.serialNo?.trim().length > 50)) {
    throw new Error("Serial number is invalid.");
  }
    setShowTableLoader(true);
    //setShowLoader(true);
  if(newRow){
    try {
  setIsSubmitting(true);
  const request = {
    id: newRow.id,
    userId: userId,
    clientId: clientId,
    tagId:newRow.tagId,
    isSerialized: newRow.isSerialized,
    isLotted: newRow.isLotted,
    isCartoned: newRow.isCartoned,
    isPalletized: newRow.isPalletized,
    serialNo: newRow.serialNo?.trim(),
    lot: newRow.lot?.trim(),
    carton: newRow.carton?.trim(),
    pallet: newRow.pallet?.trim()
  };
  const response = await ContainerizedDetailService.updateContainerizedDetail(request);
  if(response?.data?.isSuccess){
      setSnackbarMessage("Updated successfully!");
      setSeverity("success");
      setOpenSnackbar(true);
      fetchData(true);
    
      setUpdateCount(updateCount+1);
      setTimeout(() => {
        handleCloseSnackbar();
      }, 2000);
  }
} catch (error: any) {
      const errorMessages = error.response?.data?.errors || [
        "updateContainerizedDetail: Error occurred while updating containerized detail.",
      ];
      setSeverity("error");
      setSnackbarMessage(errorMessages.join(", "));
      setOpenSnackbar(true);
      console.error(error);
    }finally{
      setIsSubmitting(false);
    }
  }
  setTimeout(() => {
    setShowTableLoader(false);
    //setShowLoader(false);
  }, 2000);
  return newRow;
}
const getToBeFocusedField = (id: GridRowId): string => {
  const row = apiRef.current?.getRow(id) as any;
  if (!row) return 'pallet';

  return (
    containerType === ContainerType.Pallet ? 'pallet' :
    containerType === ContainerType.Carton ? 'carton' :
    containerType === ContainerType.Lot     ? 'lot' :
    containerType === ContainerType.SerialNo? 'serialNo' :
    row.isPalletized                         ? 'pallet' :
    row.isCartoned                           ? 'carton' :
    row.isLotted                             ? 'lot' :
    row.isSerialized                         ? 'serialNo' :
    'pallet'
  );
};
const FilledButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#3E4259", // dark filled color
  borderRadius: 8,
  color: "#fff",
  width: 35,
  height: 32,
  '&:hover': {
    backgroundColor: "#343852",
  },
}));
const BorderedButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "transparent",
  border: "1px solid #3E4259",
  borderRadius: 8,
  color: "#3E4259",
  width: 35,
  height: 32,
  '&:hover': {
    backgroundColor: "rgba(62, 66, 89, 0.04)",
  },
}));
const SimpleButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "transparent",
  borderRadius: 8,
  color: "#3E4259",
  width: 40,
  height: 46,
  '&:hover': {
    backgroundColor: "rgba(62, 66, 89, 0.04)",
  },
}));
const AcceptButton = styled(IconButton)({
  backgroundColor: "#41992F",
  borderRadius: 4,
  width: 60,
  fontSize:12,
  fontWeight:600,
  height: 24,
  color: "#fff",
  padding: 0,
  "&:hover": { backgroundColor: "#199700ff" },
});
const NoRowsOverlay = () => (
  <Box sx={{ padding: 2, textAlign: 'center' }}>
    No exceptions to resolve 😊
  </Box>
);
const columns: GridColDef[] = [
  { field: "id", headerName: "Id"},

  { field: "areaName", headerName: "Area", minWidth: 150, flex: 1 , 
    renderCell: (params) => {
      return (
        <Tooltip
          title={<span style={{ whiteSpace: "pre" }}>{params.value}</span>}
          placement="bottom"
          arrow
        >
          <span className={styles.truncatedText}>{params.value}</span>
        </Tooltip>
      );
  }, },
  { field: "locationName", headerName: "Location", minWidth: 150, flex: 1,
    renderCell: (params) => {
      return (
       <Tooltip title={
    <span style={{ whiteSpace: "pre" }}>
      {params.value}
    </span>
  } placement="bottom" arrow>
                <span className={styles.truncatedText}>
                 {params.value}
                </span>
              </Tooltip>
    );
  }
   },
  { field: "itemName", headerName: "Item", minWidth: 150, flex: 1,
    renderCell: (params) => {
      return (
       <Tooltip title={
    <span style={{ whiteSpace: "pre" }}>
      {params.value}
    </span>
  } placement="bottom" arrow>
                <span className={styles.tooltip_div}>
                 {params.value}
                </span>
              </Tooltip>
    );
  }
   },
  { field: "tagNumber", headerName: "Tag Id", minWidth: 150, flex: 1,
    renderCell: (params) => {
      return (
       <Tooltip title={
    <span style={{ whiteSpace: "pre" }}>
      {params.value}
    </span>
  } placement="bottom" arrow>
                <span className={styles.truncatedText}>
                 {params.value}
                </span>
              </Tooltip>
    );
  }
   },

  {
    field: "pallet", headerName: "Pallet", minWidth: 120, flex: 1, editable: true,
   renderCell: (params) => {
     if (!params.row.isPalletized) return "-";
    return (
       <Tooltip title={
    <span style={{ whiteSpace: "pre" }}>
      {params.value}
    </span>
  } placement="bottom" arrow>
                <span className={styles.truncatedText}>
                 {params.value}
                </span>
              </Tooltip>
    );
  },
    preProcessEditCellProps: async (params) => {
      const value = params.props.value?.toString() || "";
      const hasError = params.row.isPalletized && value.length > 50;
      if(hasError && !openSnackbar && value.trim()){
        toasterMessage("error","Maximum 50 characters allowed.");
      }
      return { ...params.props, error: hasError };
    }
  },

  {
    field: "carton", headerName: "Carton", minWidth: 120, flex: 1, editable: true,
   
    renderCell: (params) => {
     if (!params.row.isCartoned) return "-";
    return (
       <Tooltip title={
    <span style={{ whiteSpace: "pre" }}>
      {params.value}
    </span>
  } placement="bottom" arrow>
                <span className={styles.truncatedText}>
                 {params.value}
                </span>
              </Tooltip>
    );
  },
    preProcessEditCellProps: async (params) => {
      const value = params.props.value?.toString() || "";
      const hasError = params.row.isCartoned && value.length > 50;
            if(hasError && !openSnackbar && value.trim()){
        toasterMessage("error","Maximum 50 characters allowed.");
      }
      return { ...params.props, error: hasError };
    }
  },

  {
    field: "lot", headerName: "Lot", minWidth: 120, flex: 1, editable: true,
     renderCell: (params) => {
     if (!params.row.isLotted) return "-";
    return (
       <Tooltip title={
    <span style={{ whiteSpace: "pre" }}>
      {params.value}
    </span>
  } placement="bottom" arrow>
                <span className={styles.truncatedText}>
                 {params.value}
                </span>
              </Tooltip>
    );
  },
    preProcessEditCellProps: async (params) => {
      const value = params.props.value?.toString() || "";
      const hasError = (params.row.isLotted && !value.trim()) || value.length > 50;
            if(hasError && !openSnackbar && value.trim()){
        toasterMessage("error","Maximum 50 characters allowed.");
      }
      return { ...params.props, error: hasError };
    }
  },

  {
    field: "serialNo", headerName: "Serial", minWidth: 150, flex: 1, editable: true,
renderCell: (params) => {
     if (!params.row.isSerialized) return "-";
    return (
       <Tooltip title={
    <span style={{ whiteSpace: "pre" }}>
      {params.value}
    </span>
  } placement="bottom" arrow>
                <span className={styles.truncatedText}>
                 {params.value}
                </span>
              </Tooltip>
    );
  },
    preProcessEditCellProps: async (params) => {
      const value = params.props.value?.toString() || "";
      const hasError = (params.row.isSerialized && !value.trim()) || value.length > 50;
            if(hasError && !openSnackbar && value.trim()){
        toasterMessage("error","Maximum 50 characters allowed.");
      }
      return { ...params.props, error: hasError };
    }
  },

  {
    field: "qty", headerName: "Qty", flex: 1, minWidth: 100,
    renderCell: (params) => {
    if (params.row.isSerialized) return '\u00A0';

    return (
      <Tooltip
        title={
          <span style={{ whiteSpace: "pre" }}>
            {params.value}
          </span>
        }
        placement="bottom"
        arrow
      >
        <span
          className={
           styles.truncatedText
          }
        >
          {params.value}
        </span>
      </Tooltip>
    );
  }
},

  {
    field: "actions", headerName: "Action", minWidth: 150, flex: 0.5,
    type: "actions",
    renderCell: (params) => {
      const isInEdit = rowModesModel[params.id]?.mode === GridRowModes.Edit;
      return isInEdit ? (
        <>
          <FilledButton onClick={() => handleSaveClick(params.id)}>
            <Icon path={mdiCheck} size={1.2} />
          </FilledButton>
          <BorderedButton onClick={() => handleCancelClick(params.id)} sx={{ ml: 1 }}>
            <Icon path={mdiClose} size={1.2} />
          </BorderedButton>
        </>
      ) : (
        <>
          <SimpleButton onClick={() => handleEditClick(params.id)}>
            <Icon path={mdiSquareEditOutline} size={0.9} />
          </SimpleButton>
          <AcceptButton onClick={() => setAcceptDialogOpen(true)}>
            Accept
          </AcceptButton>
        </>
      );
    }
  }
];
const getModelTitle = (): string => {
  const titles = ExceptionTypes
    .filter(e => e.containerType === containerType)
    .map(e => e.exceptionResolveTitle);

  return titles.length > 0 ? titles.join(" / ") : "";
};
const getConfirmationMsg = (): string => {
  const titles = ExceptionTypes
    .filter(e => e.containerType === containerType)
    .map(e => e.exceptionResolveConfirmationMsg);

  return (titles.length > 0 ? titles.join(" / ") : "").replace("<SelectedValue>",selectedValue ??"");
};
//<-- End: Table properties and events -->
  return (
    <DialogStyle
      open={open}
      fullWidth
      maxWidth={false}
     sx={{
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "90vw",
      maxWidth: "90vw",
    },
  },
}}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>{getModelTitle()}</DialogTitle>
      <IconButton
        aria-label="close"
        disabled={isSubmitting}
        onClick={handleClickClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "#FFF",
        }}
      >
        <Icon path={mdiClose} size={1} />
      </IconButton>
<DialogContent
  sx={{
    padding: 2,
    overflow: showLoader ? 'hidden' : 'auto',
    maxHeight: '75vh',
  }}
>
        <Grid container spacing={2}>
          {/* <Box>
              {getModelTitle()}
            </Box> */}
      
        {rows && rows.length > 0 && !showLoader && (
          <Box sx={{ width: '100%', maxHeight: '65vh', overflowY: 'auto' }}>
          <DataGrid
                      apiRef={apiRef}
                      rows={rows}
                      columns={columns}
                      getRowId={(row) => row.id}
                      editMode="row"
                      rowModesModel={rowModesModel}
                      onRowModesModelChange={(newModel) => {
                        setRowModesModel(newModel);

                        const newValidity: Record<GridRowId, boolean> = {};

                        Object.entries(newModel).forEach(([id, modeProps]) => {
                          if (modeProps.mode === GridRowModes.Edit) {
                            const row = apiRef.current?.getRow(id);
                            if (row) {
                              const lotInvalid = row.isLotted && !row.lot?.trim();
                              const serialInvalid =
                                row.isSerialized && !row.serialNo?.trim();
                              newValidity[id] = !(lotInvalid || serialInvalid);
                            }
                          }
                        }); 

                        setIsRowValid(newValidity);
                      }}
                      processRowUpdate={processRowUpdate}
                      columnVisibilityModel={columnVisibilityModel}
                      onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibilityModel(newModel)
                      }
                      onCellKeyDown={(params, event) => {
                        if (
                          event.key === "Enter" &&
                          rowModesModel[params.id]?.mode === GridRowModes.Edit
                        ) {
                          saveIntentRef.current[params.id] = true;
                          (apiRef.current as GridApi).stopRowEditMode({
                            id: params.id,
                            ignoreModifications: false,
                          });
                        }
                      }}
                      onProcessRowUpdateError={(error) => {
                        toasterMessage("error",error.message);
                      }}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: rows.length > 0 ? rows.length : 100,
                            page: 0,
                          },
                        },
                      }}
                      hideFooterSelectedRowCount
                      disableColumnMenu
                      isCellEditable={(params) => {
                        const { field, row } = params;
                        if (field === "serialNo" && !row.isSerialized) return false;
                        if (field === "carton" && !row.isCartoned) return false;
                        if (field === "lot" && !row.isLotted) return false;
                        if (field === "pallet" && !row.isPalletized) return false;
                        return true;
                      }}
                      hideFooter
                      getCellClassName={(params) => {
                        if (
                          params.field === "lot" &&
                          params.row.isLotted &&
                          !params.value?.trim()
                        ) {
                          return "error-cell";
                        }
                        if (
                          params.field === "serialNo" &&
                          params.row.isSerialized &&
                          !params.value?.trim()
                        ) {
                          return "error-cell";
                        }
                        return "";
                      }}
                      sx={{
                        width: "100%",
                        "& .MuiDataGrid-columnHeader, .MuiDataGrid-columnHeaderTitle":
                          {
                            backgroundColor: "#d9dde8",
                          },
                        "& .MuiDataGrid-sortIcon": {
                          opacity: 1,
                        },
                        "& .MuiDataGrid-menuIconButton": {
                          opacity: 1,
                        },
                        "& .highlight-row": {
                        backgroundColor:"lightgrey"
                        },
                        "& .highlight-row:hover": {
                          backgroundColor:"lightgrey"
                        },
                        " & .MuiDataGrid-row--editing.highlight-row": {
                          backgroundColor:"lightgrey"
                        },
                        " & .MuiDataGrid-row--editing.highlight-row:hover": {
                          backgroundColor:"lightgrey"
                        },
                        // Also style the cells within the editing row
                        " & .MuiDataGrid-row--editing.highlight-row .MuiDataGrid-cell":
                          {
                            bgcolor: "inherit",
                          },
                        "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                          outline: "none !important",
                        },
                        "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within": {
                          outline: "none !important",
                        },
                        "& .error-cell .MuiInputBase-root": {
                          border: "1px solid red !important",
                        },
                        "& .MuiDataGrid-cell--editing .MuiInputBase-input": {
                          fontFamily: '"Inter", sans-serif',
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: "600",
                          color: "#494949 !important", // Header text color
                        },
                        "@media (max-width: 600px)": {
                          "& .MuiDataGrid-row": {
                            fontSize: "12px", // Smaller font size for rows on mobile
                            padding: "4px", // Adjust padding for mobile
                          },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: "600",
                            color: "#494949 !important", // Header text color
                          },
                        },
                        "& .MuiDataGrid-row--borderBottom": {
                          backgroundColor: "#D9DDE8 !important", // Header background color
                          color: "#494949 !important", // Header text color
                        },
                        borderRadius: "0px",
                        border: "none",
                        "& .MuiDataGrid-columnHeaderRow": {
                          background: "#D9DDE8 !important", // Header background color
                          color: "#494949 !important", // Header text color
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          position: "sticky",
                          top: 0,
                          zIndex: 1, // Ensure the header stays on top
                          backgroundColor: "#D9DDE8 !important", // Header background color
                          color: "#494949 !important", // Header text color
                        },
                        "& .MuiDataGrid-columnSeparator": {
                          display: "none", // Remove column separators
                        },
                        "& .MuiDataGrid-cell--editing": {
                          backgroundColor: "#fff",
                          padding: 0,
                        },
                        "& .MuiDataGrid-cell--editing .MuiInputBase-root": {
                          borderRadius: 2,
                          border: "1px solid #ccc",
                          backgroundColor: "#fff",
                          "&:hover": {
                            borderColor: "#888",
                          },
                          "&.Mui-error": {
                            borderColor: (theme) => theme.palette.error.main,
                          },
                          boxSizing: "border-box",
                        },
                         "& .MuiDataGrid-cellEmpty": {
      display: "none",
    },
                         "& .MuiDataGrid-filler": {
      display: "none",
    },
                      }}
                      onCellDoubleClick={(params, event) => {
                        event.defaultMuiPrevented = true;
                      }}
                      loading={showTableLoader}
                      slots={{
                  // ),
                  noRowsOverlay: NoRowsOverlay,
                }}
                scrollbarSize={1} 
              />
          </Box>
           )}
          {(!rows || rows?.length < 1) && !showLoader && (
          <Box sx={{ width: '100%', height:'100px',display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <NoRowsOverlay />
  </Box>
          )}
          

          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={openSnackbar}
            autoHideDuration={10000}
            onClose={handleCloseSnackbar}
          >
            <Alert icon={getIcon()} severity={severity}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
          {accepDialogOpen &&
                            ( <ConfirmationDialog
                              count={null}
                              title={"Accept exceptions"}
                              message={getConfirmationMsg()}
                              open={accepDialogOpen}
                              onClose={() => {setAcceptDialogOpen(false)}}
                              onConfirm={() => {
                                setAcceptDialogOpen(false);
                                handleAcceptException();
                                              }}
                            
                                          />)
                        }
        </Grid>
{showLoader && <DialogLoader />}

      </DialogContent>
    </DialogStyle>
  );
};

export default ResolveExceptionDialog;
